import { createDraftSafeSelector } from '@reduxjs/toolkit'
import { OrderBook, Security } from '../proto/generated/security'
import { SecurityStateType, Snapshot } from '../reducer/security.reducer'
import store, { RootState } from '../store'
import { isValidExchange } from '../util/security'
import { formatGreeks, greekColumns } from '../pages/home/optionChainViewTabs/components/DynamicColumnRender'
import { Exchange } from '../proto/generated/common'

const state = (state: RootState) => state

type CandleChangeSelector<T> = (state: RootState) => T

/** Stable selector identity per security field — plain functions, not createSelector (avoids NaN / ref warnings). */
const candleChangeSelectorCache = new Map<string, CandleChangeSelector<unknown>>()

const getCandleChangeSelector = <T,>(cacheKey: string, selectValue: (state: RootState) => T): CandleChangeSelector<T> => {
    let selector = candleChangeSelectorCache.get(cacheKey) as CandleChangeSelector<T> | undefined
    if (!selector) {
        selector = (s: RootState) => selectValue(s)
        candleChangeSelectorCache.set(cacheKey, selector as CandleChangeSelector<unknown>)
    }
    return selector
}

const readImpliedVolatility = (iv: number | undefined): number | undefined => {
    if (iv === undefined || Number.isNaN(iv)) return undefined
    return Number(iv.toFixed(2))
}

/** Scalar that changes when any live candle field updates — safe for busting memoized table bodies. */
export const selectCandleChangeBookRevision = createDraftSafeSelector(
    (s: RootState) => s.securityStore.candleChangeBook,
    (book): number => {
        let revision = 0
        for (const key in book) {
            const row = book[key]
            if (!row) continue
            revision +=
                (row.ltp ?? 0) +
                (row.topBid ?? 0) +
                (row.topAsk ?? 0) +
                (row.openInterest ?? 0) +
                (row.ltpChangePercent ?? 0)
        }
        return revision
    }
)

export const selectOverviewSecurity = createDraftSafeSelector(
    (state: RootState) => state.securityStore.overviewSecurity,
    (overviewSecurity) => overviewSecurity
)

export const selectExpressionData = createDraftSafeSelector(state, (state: RootState) => state.securityStore.expression)
export const selectExpressionSecurities = createDraftSafeSelector(state, (state: RootState): Record<string, Security> => {
    const data: Record<string, Security> = {};
    const { expression: { expressionSymbols }, securities } = state.securityStore
    return expressionSymbols.reduce((acc: Record<string, Security>, key: string) => {
        acc[key] = securities[key]
        return acc
    }, data)
})

export const selectExpressionSnapshots = createDraftSafeSelector(state, (state: RootState): Record<string, Snapshot> => {
    const { expression: { expressionSymbols }, snapshots } = state.securityStore
    const result: Record<string, Snapshot> = {}
    return expressionSymbols.reduce((acc: Record<string, Snapshot>, key: string) => {
        acc[key] = snapshots[key];
        return acc
    }, result)
})

export const selectUserSecurityExpressions = createDraftSafeSelector(state, (state: RootState) => {
    const { userExpressions } = state.securityStore
    return userExpressions
})
export const selectSecuritiesLoaded = createDraftSafeSelector(state, (state: RootState): Boolean => state.securityStore.securitiesLoaded)
export const selectPriceMultiplier = createDraftSafeSelector(state, (state: RootState) => state.securityStore?.securities?.["NSE:NIFTY_50"]?.priceMultiplier??100 as number)
export const selectLastRefreshTime = createDraftSafeSelector(state, (state: RootState): SecurityStateType['snapshots'] => state.securityStore.lastRefreshTime)
export const selectSnapshots = createDraftSafeSelector(state, (state: RootState): SecurityStateType['snapshots'] => state.securityStore.snapshots)
const snapshotSelectorCache = new Map<string, ReturnType<typeof createDraftSafeSelector>>()

export const selectSnapshot = (key: string) => {
    let selector = snapshotSelectorCache.get(key)
    if (!selector) {
        selector = createDraftSafeSelector(
            (s: RootState) => s.securityStore.snapshots[key],
            (snapshot) => snapshot
        )
        snapshotSelectorCache.set(key, selector)
    }
    return selector
}

const readPositionLtp = (s: RootState, key: string): number => {
    const book = s.securityStore.candleChangeBook?.[key]
    if (book?.ltp != null) return book.ltp
    return s.securityStore.snapshots[key]?.lastTradedPrice ?? 0
}

/** Stable selector per key set — output changes only when LTP for one of these securities changes. */
const positionLtpsSignatureCache = new Map<string, ReturnType<typeof createDraftSafeSelector>>()

const quickTradeQuoteSignatureCache = new Map<string, ReturnType<typeof createDraftSafeSelector>>()

/** Scalar quote fields for one security — output changes only when that symbol's live quote changes. */
const oiStrikeFilterLtpCache = new Map<string, ReturnType<typeof createDraftSafeSelector>>()

/** Rounded underlying LTP (50pt steps) — re-renders only when strike window may change. */
export const selectOiStrikeFilterLtp = (key: string) => {
    let selector = oiStrikeFilterLtpCache.get(key)
    if (!selector) {
        selector = createDraftSafeSelector(
            (s: RootState) => {
                const bookLtp = s.securityStore.candleChangeBook?.[key]?.ltp
                const snapLtp = s.securityStore.snapshots[key]?.lastTradedPrice
                const ltp = bookLtp ?? snapLtp ?? 0
                return Math.round(ltp / 50) * 50
            },
            (rounded) => rounded
        )
        oiStrikeFilterLtpCache.set(key, selector)
    }
    return selector
}

export const selectQuickTradeQuoteSignature = (key: string) => {
    let selector = quickTradeQuoteSignatureCache.get(key)
    if (!selector) {
        selector = createDraftSafeSelector(
            (s: RootState) => {
                const book = s.securityStore.candleChangeBook?.[key]
                const snap = s.securityStore.snapshots[key]
                return `${book?.topBid ?? 0}\0${book?.topAsk ?? 0}\0${book?.ltp ?? 0}\0${snap?.lastTradedPrice ?? 0}\0${snap?.volume ?? 0}`
            },
            (signature) => signature
        )
        quickTradeQuoteSignatureCache.set(key, selector)
    }
    return selector
}

export const selectPositionLtpsSignature = (keys: readonly string[]) => {
    const cacheKey = keys.join('\x1e')
    let selector = positionLtpsSignatureCache.get(cacheKey)
    if (!selector) {
        selector = createDraftSafeSelector(
            (s: RootState) => keys.map((k) => readPositionLtp(s, k)).join('\0'),
            (signature) => signature
        )
        positionLtpsSignatureCache.set(cacheKey, selector)
    }
    return selector
}

/** Stable selector per key set — output changes only when greeks for one of these securities change. */
const positionGreeksSignatureCache = new Map<string, ReturnType<typeof createDraftSafeSelector>>()

export const selectPositionGreeksSignature = (keys: readonly string[]) => {
    const cacheKey = keys.join('\x1e')
    let selector = positionGreeksSignatureCache.get(cacheKey)
    if (!selector) {
        selector = createDraftSafeSelector(
            (s: RootState) => {
                const greeks = s.securityStore.greeks
                return keys
                    .map((k) => {
                        const g = greeks[k]
                        if (!g) return ''
                        return `${g.delta ?? 0}|${g.theta ?? 0}|${g.gamma ?? 0}|${g.vega ?? 0}`
                    })
                    .join('\0')
            },
            (signature) => signature
        )
        positionGreeksSignatureCache.set(cacheKey, selector)
    }
    return selector
}

export const selectGreeks = createDraftSafeSelector(state, (state: RootState): SecurityStateType['greeks'] => state.securityStore.greeks)
export const selectOrderBooks = createDraftSafeSelector(state, (state: RootState): SecurityStateType['orderBooks'] => state.securityStore.orderBooks)

const orderBookSelectorCache = new Map<string, CandleChangeSelector<OrderBook | undefined>>()

export const selectOrderBook = (key: string): CandleChangeSelector<OrderBook | undefined> => {
    let selector = orderBookSelectorCache.get(key)
    if (!selector) {
        selector = createDraftSafeSelector(state, (state: RootState): OrderBook | undefined => state.securityStore.orderBooks[key])
        orderBookSelectorCache.set(key, selector)
    }
    return selector
}
export const selectOrderBookTopBid = (key: string) =>
    getCandleChangeSelector(`topBid:${key}`, (state) => state.securityStore.candleChangeBook?.[key]?.topBid)
export const selectOrderBookTopAsk = (key: string) =>
    getCandleChangeSelector(`topAsk:${key}`, (state) => state.securityStore.candleChangeBook?.[key]?.topAsk)
export const selectCandleChangeLtp = (key: string) =>
    getCandleChangeSelector(`ltp:${key}`, (state) => state.securityStore.candleChangeBook?.[key]?.ltp)
export const selectCandleChangeLtpChange = (key: string) =>
    getCandleChangeSelector(`ltpChange:${key}`, (state) => state.securityStore.candleChangeBook?.[key]?.ltpChange)
export const selectCandleChangeLtpChangePercent = (key: string) =>
    getCandleChangeSelector(`ltpChangePercent:${key}`, (state) => state.securityStore.candleChangeBook?.[key]?.ltpChangePercent)
export const selectCandleChangeOpenInterest = (key: string) =>
    getCandleChangeSelector(`openInterest:${key}`, (state) => state.securityStore.candleChangeBook?.[key]?.openInterest)
export const selectCandleChangeOpenInterestChange = (key: string) =>
    getCandleChangeSelector(`oiChange:${key}`, (state) => state.securityStore.candleChangeBook?.[key]?.oiChange)
export const selectReferenceSecurityMap = createDraftSafeSelector(state, (state: RootState): SecurityStateType['referenceSecurityMap'] => state.securityStore.referenceSecurityMap)
export const selectCandleChangeIv = (key: string) =>
    getCandleChangeSelector(`iv:${key}`, (state) => readImpliedVolatility(state.securityStore.candleChangeBook?.[key]?.impliedVolatility))
export const selectCandleChangeField = (key: string, field: string) =>
    getCandleChangeSelector(`field:${field}:${key}`, (state: RootState): number | string | undefined => {
        const row = state.securityStore.candleChangeBook?.[key]
        if (!row) return undefined
        if (greekColumns.includes(field)) {
            const greek = row.optionGreeks?.[field as keyof typeof row.optionGreeks]
            if (greek === undefined || greek === null || Number.isNaN(greek)) return undefined
            if (field === 'gamma') return greek.toFixed(4)
            return formatGreeks(greek)
        }
        return (row as Record<string, number | string | undefined>)[field]
    })
/** Depends on companyCode only — not Redux ticks (data comes from securityManager). */
export const selectExchangesByCompanyCode = (companyCode: string) =>
    createDraftSafeSelector(
        () => companyCode,
        (code): Exchange[] => {
            if (!code) return []
            const exchanges = window.securityManager
                .getAllSecurities()
                .filter((sec) => sec.companyCode === code)
                .map((sec) => sec.exchange)
                .filter(isValidExchange)
            return Array.from(new Set(exchanges))
        }
    )

export const getSecuritySnapshot = (key: string): Snapshot | undefined => {
    const state = store.getState()
    return state?.securityStore?.snapshots[key]
}

export const getOrderBook = (key: string): OrderBook | undefined => {
    const state = store.getState()
    return state?.securityStore?.orderBooks?.[key]
}
